import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Text, Button } from '@cuemath/web-comps';
import { COLORS } from '@cuemath/comps';

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 40px;
  text-align: center;
`;

const ErrorTitle = styled(Text)`
  font-weight: 600;
  color: ${COLORS.RED_DARK};
  margin-bottom: 16px;
`;

const ErrorMessage = styled(Text)`
  color: ${COLORS.GRAY_DARK};
  margin-bottom: 24px;
  max-width: 600px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // You can also log to an error reporting service here
    // e.g., Sentry.captureException(error, { extra: errorInfo });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <ErrorContainer>
          <ErrorTitle variant="h5">
            {this.props.title || 'Oops! Something went wrong'}
          </ErrorTitle>
          <ErrorMessage variant="body1Regular">
            {this.props.message ||
              'An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.'}
          </ErrorMessage>
          <ButtonGroup>
            <Button onClick={this.handleReset}>Try Again</Button>
            <Button onClick={this.handleReload} variant="secondary">
              Reload Page
            </Button>
          </ButtonGroup>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{ marginTop: '20px', textAlign: 'left', width: '100%' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                Error Details (Development Only)
              </summary>
              <pre
                style={{
                  marginTop: '10px',
                  padding: '10px',
                  background: '#f5f5f5',
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '200px',
                  fontSize: '12px',
                }}
              >
                {this.state.error.toString()}
                {'\n\n'}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  title: PropTypes.string,
  message: PropTypes.string,
};

ErrorBoundary.defaultProps = {
  fallback: null,
  title: null,
  message: null,
};

export default ErrorBoundary;
